import * as React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid, TextField, Typography,
  Link, Avatar, ListItem, ListItemAvatar, ListItemText, useTheme, useMediaQuery,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import { Alert } from '@material-ui/lab';
import { debounce } from 'lodash';

import clsx from 'clsx';
import useEventListener from '../../lib/useEventListener';
import deselectElementPreventDefaultAndBlur from '../../lib/deselectAndBlur';
import RepairCardCarousel from '../metalCollection/RepairCardCarousel';
import GenericCard from '../global/GenericCard';
import globalUseStyles from '../../globalClasses';

interface IWayfinderRepair {
  repairId: string;
  barcode: string;
  shipByDate: Date;
  currentLocation: string;
  shipLog: boolean;
  company: string;
  legacyRepairId: string;
}

const Wayfinder: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const globalStyles = globalUseStyles();
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [openRepairProcessing, setOpenRepairProcessing] = React.useState<boolean>(false);
  const [selectedBarcode, setSelectedBarcode] = React.useState<string>();
  const [repairId, setRepairId] = React.useState<string>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getRepairId = (repairBarcodeParam: string) => {
    setOpenRepairProcessing(true);
    axios.get<{ repairId:string }>('/Wayfinder/ScanRepair', {
      params: {
        barcode: repairBarcodeParam,
      },
    })
      .then((response) => {
        setRepairId(response.data.repairId);
        setOpenModal(true);
        return response.data.repairId;
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      })
      .finally(() => setOpenRepairProcessing(false));
  };

  const repairFetcher = (url: string, repairIdParam: string) => axios.get<IWayfinderRepair>(url, {
    params: {
      repairId: repairIdParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const { data } = useSWR(repairId ? ['/Wayfinder/GetWayfinderRepair', repairId] : null, (url, repairIdParam) => repairFetcher(url, repairIdParam));

  let bannerBuffer = '';
  let honeywellBuffer = '';

  const handleBannerBarcodeInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      bannerBuffer = `${bannerBuffer}${e.key}`;
      if (bannerBuffer.toLowerCase().includes('in:')) {
        deselectElementPreventDefaultAndBlur(e);
        bannerBuffer = '';
      }
      if (bannerBuffer.includes('{*|}')) {
        deselectElementPreventDefaultAndBlur(e);
        setSelectedBarcode(bannerBuffer.replace('{*|}', ''));
        getRepairId(bannerBuffer.replace('{*|}', ''));
        bannerBuffer = '';
        honeywellBuffer = '';
      }
      if (bannerBuffer.toLowerCase().includes('out:')) {
        deselectElementPreventDefaultAndBlur(e);
        bannerBuffer = '';
      }
      if (bannerBuffer.includes('<>??<>')) {
        deselectElementPreventDefaultAndBlur(e);
        bannerBuffer = '';
      }
    }
  };

  const handleHoneywellBarcodeInput = (e: KeyboardEvent) => {
    if (e.key && (e.key.length === 1 || e.key === 'Enter')) {
      if (e.key !== 'Enter') {
        honeywellBuffer = `${honeywellBuffer}${e.key}`;
      } else {
        if (!e.defaultPrevented) {
          honeywellBuffer.replace('{*|}', '');
          setSelectedBarcode(honeywellBuffer.replace('{*|}', ''));
          getRepairId(honeywellBuffer.replace('{*|}', ''));
          honeywellBuffer = '';
        }
        bannerBuffer = '';
        honeywellBuffer = '';
      }
    }
  };

  const scanBarcode = debounce((scannedBarcode: string) => {
    // if there's no content
    if (!scannedBarcode) {
      return;
    }
    setSelectedBarcode(scannedBarcode);
    getRepairId(scannedBarcode);
  }, 250);

  useEventListener('keydown', handleBannerBarcodeInput);
  useEventListener('keydown', handleHoneywellBarcodeInput);

  if (!data) {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'To get started, scan in a repair.'}</Alert>
        </Grid>
        <Grid item>
          {openRepairProcessing
            ? (
              <CircularProgress />
            )
            : (
              <TextField
                variant="outlined"
                size="small"
                onChange={(event) => {
                  if (event.target.value.length > 9) {
                    scanBarcode(event.target.value);
                  }
                }}
                onKeyPress={
                  (event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.stopPropagation();
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                      scanBarcode((event.target as HTMLInputElement).value);
                    }
                  }
                  }
              />
            )}
        </Grid>
      </Grid>
    );
  }

  return (
    <Dialog
      open={openModal}
      onClose={() => { setOpenModal(false); setSelectedBarcode(undefined); setRepairId(undefined); }}
      fullWidth
      maxWidth="lg"
    >
      <DialogContent>
        <Box m={1}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                {`Repair: ${data.barcode}`}
              </Typography>
            </Grid>
            <Grid item xs={isMobile ? 12 : 2}>
              <Grid
                container
                direction="column"
                alignItems="stretch"
                spacing={2}
                style={{ height: '100%' }}
              >
                <Grid item xs={isMobile ? 12 : 10}>
                  <Typography variant="h6" color="textSecondary">{`Expected Ship Date: ${data.shipByDate ? new Date(data.shipByDate).toLocaleDateString() : 'None'}`}</Typography>
                </Grid>
                <Grid item xs={isMobile ? 12 : 2}>
                  <Typography variant="h6" color="textSecondary">{data.company}</Typography>
                </Grid>
                {data.shipLog && (
                <Grid item>
                  <ListItem dense disableGutters>
                    <ListItemAvatar style={{ minWidth: '1rem', paddingRight: '0.5rem' }}>
                      <Avatar className={clsx(globalStyles.smallAvatar, globalStyles.redIcon)}>
                        <LocalShippingIcon className={clsx(globalStyles.smallIconSize)} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="h6" color="textSecondary">{'Ship Log'}</Typography>}
                    />
                  </ListItem>
                </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={isMobile ? 12 : 10}>
              <Box mx={isMobile ? 0 : 6}>
                <RepairCardCarousel
                  key={selectedBarcode}
                  barcode={selectedBarcode || ''}
                  hideUnderCard
                />
              </Box>
            </Grid>
            <Grid item xs={6} />
            {process.env.REACT_APP_JEMS_ONE_URL && (
            <Grid item xs={6} onClick={(event) => { console.log(event.target); }}>
              <Link href={`${process.env.REACT_APP_JEMS_ONE_URL}/Repair/UpdateRepair.aspx?rid=${data.legacyRepairId}`} target="_blank" rel="noreferrer noopener">
                <Typography variant="h6" color="primary">{'View in JEMS 1'}</Typography>
              </Link>
            </Grid>
            )}
            <Grid item xs={6}>
              <GenericCard
                primaryText="Current Location"
                secondaryText={data.currentLocation}
              >
                <LocationOnIcon />
              </GenericCard>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        {/* <Button onClick={() => {}}>{'Claim'}</Button> */}
        <Button color="primary" variant="contained" onClick={() => { setOpenModal(false); setSelectedBarcode(undefined); setRepairId(undefined); }}>{'Close'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default Wayfinder;
